<script>
  //// imports
  import { createEventDispatcher } from "svelte";
  import AccordionPagination from "lib/components/accordionPagination.svelte";
  import SideKPI from "../../../../lib/components/sideKPI.svelte";
  import ButtonNew from "../../../../lib/components/ButtonNew.svelte";
  import { Tooltip } from "paxray-components-svelte";

  export let subtaskList = [];
  export let currentSubtask = "";
  export let loading = false;
  export let enableDeselection = true;
  export let enableSorter = false;
  export let sorterByNumber = "desc"; //"", "desc, "asc"
  export let sorterByName = ""; //"", "desc, "asc"
  export let taskNameById = {};
  export let taskGroupsByLevel = {};
  export let valueLabel = "number";
  export let valueSuffix = "Rec.";
  export let disableThreshold = 3;
  export let showSelectedTaskName = false;
  export let showFrequencyMapping = false;

  const dispatch = createEventDispatcher();

  let displayCount = 5; // default value for number of apps shown
  let currentPage = 1;
  let displayedTaskGroups = [];
  let openTaskGroups = {};
  let taskSettingsOpen = false;
  let taskGroupingLevel = "L2";
  let showUnavailableTasks = false;
  let hiddenUnavailableTaskCount = 0;

  let searchTerm = "";

  $: updatedSubtaskList(subtaskList);

  $: if (
    currentPage ||
    searchTerm ||
    subtaskList ||
    taskGroupsByLevel ||
    taskGroupingLevel ||
    openTaskGroups ||
    currentSubtask ||
    sorterByNumber ||
    sorterByName ||
    showUnavailableTasks ||
    true
  )
    updateDisplay();

  function selectTask(task) {
    if (enableDeselection) {
      currentSubtask === task.id
        ? (currentSubtask = "")
        : (currentSubtask = task.id);
    } else currentSubtask = task.id;
  }

  let filteredTasks = [];
  $: taskGroupCount = groupTasks(
    filteredTasks,
    taskGroupsByLevel,
    taskGroupingLevel,
  ).length;
  $: selectedTaskName =
    subtaskList.find((task) => task.id === currentSubtask)?.name || "";
  $: taskToplineText = `${filteredTasks.length} Tasks${
    showSelectedTaskName && selectedTaskName ? ` - ${selectedTaskName}` : ""
  }${
    !showUnavailableTasks && hiddenUnavailableTaskCount
      ? ` · ${hiddenUnavailableTaskCount} hidden`
      : ""
  }`;

  function updateDisplay() {
    let matchingTasks;
    if (searchTerm) {
      matchingTasks = subtaskList.filter((task) =>
        task.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    } else {
      matchingTasks = [...subtaskList];
    }

    hiddenUnavailableTaskCount = matchingTasks.filter(
      (task) => task.disable,
    ).length;
    filteredTasks = showUnavailableTasks
      ? matchingTasks
      : matchingTasks.filter((task) => !task.disable);

    while (filteredTasks.length < (currentPage - 1) * displayCount) {
      currentPage--;
    }
    displayedTaskGroups = groupTasks(
      sorterTasks(filteredTasks),
      taskGroupsByLevel,
      taskGroupingLevel,
    ).slice((currentPage - 1) * displayCount, currentPage * displayCount);
  }

  function sorterTasks(filteredTasks) {
    let sorterTasks;
    if (!filteredTasks.length) return [];
    if (!enableSorter || (!sorterByNumber && !sorterByName))
      sorterTasks = filteredTasks;
    else if (sorterByNumber === "asc")
      sorterTasks = filteredTasks.sort((a, b) => a.value - b.value);
    else if (sorterByNumber === "desc")
      sorterTasks = filteredTasks.sort((a, b) => b.value - a.value);
    else if (sorterByName === "asc")
      sorterTasks = filteredTasks.sort((a, b) => a.name.localeCompare(b.name));
    else if (sorterByName === "desc")
      sorterTasks = filteredTasks.sort((a, b) => b.name.localeCompare(a.name));

    return sorterTasks;
  }

  function nameSorter() {
    sorterByNumber = "";
    sorterByName = !sorterByName || sorterByName === "desc" ? "asc" : "desc";
  }

  function valueSorter() {
    sorterByName = "";
    sorterByNumber =
      !sorterByNumber || sorterByNumber === "desc" ? "asc" : "desc";
  }

  function formatTaskValue(value) {
    const rounded = Math.round(Number(value) || 0);
    const abs = Math.abs(rounded);
    if (abs >= 1_000_000) return `${(rounded / 1_000_000).toFixed(2)} M`;
    if (abs >= 1_000) return `${(rounded / 1_000).toFixed(1)} k`;
    return new Intl.NumberFormat("de-DE", {
      maximumFractionDigits: 0,
    }).format(rounded);
  }

  function formatTaskValueTooltip(value) {
    const formattedValue = formatTaskValue(value);
    return valueSuffix === "Rec."
      ? `${formattedValue} Registrations`
      : `${formattedValue} ${valueSuffix}`;
  }

  function formatTaskGroupTooltip(group) {
    return `${group.subtasks.length} Tasks\n${formatTaskValueTooltip(group.value)}`;
  }

  function updatedSubtaskList(subtaskList) {
    subtaskList = subtaskList.map((task) => {
      task.disable = task.value < disableThreshold;
      return task;
    });

    if (
      currentSubtask &&
      subtaskList.some((task) => task.id === currentSubtask && task.disable)
    ) {
      currentSubtask = "";
    }

    if (!currentSubtask && subtaskList.length > 0)
      currentSubtask = subtaskList
        .filter((task) => !task.disable)
        .sort((a, b) => b.value - a.value)[0]?.id;
  }

  function toggleTaskGroup(groupId) {
    openTaskGroups = {
      ...openTaskGroups,
      [groupId]: !openTaskGroups[groupId],
    };
  }

  function groupTasks(tasks, taskGroupsByLevel, taskGroupingLevel) {
    const groups = [];
    const groupsById = {};
    const levelGroups = taskGroupsByLevel[taskGroupingLevel] || {};
    tasks.forEach((task) => {
      const teamName = task.teams?.[0] || "Other Teams";
      const group = taskGroupingLevel === "Team"
        ? { id: teamName, name: teamName }
        : levelGroups[task.task_id] || {
            id: task.task_id || "unknown",
            name: taskNameById[task.task_id] || "Other Tasks",
          };
      if (!groupsById[group.id]) {
        groupsById[group.id] = {
          id: group.id,
          name: group.name,
          value: 0,
          subtasks: [],
        };
        groups.push(groupsById[group.id]);
      }
      groupsById[group.id].value += Number(task.value) || 0;
      groupsById[group.id].subtasks.push(task);
    });
    return groups.map((group) => ({
      ...group,
      open:
        openTaskGroups[group.id] ??
        group.subtasks.some((task) => task.id === currentSubtask),
    }));
  }
</script>

<div class="app_selection_wrapper">
  <div class="search_wrapper">
    <div class="search">
      <div class="searchicon">
        <svg
          height="100%"
          viewBox="0 0 26 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25.1466 24L14.8751 14.4151C14.077 15.0963 13.1462 15.6125 12.0828 15.9637C11.0193 16.3149 9.96862 16.4906 8.9306 16.4906C6.45541 16.4906 4.36059 15.6939 2.64614 14.1005C0.931688 12.5071 0.0744629 10.5574 0.0744629 8.25144C0.0744629 5.94545 0.928225 3.99371 2.63575 2.39623C4.3433 0.798742 6.42958 0 8.89461 0C11.3596 0 13.4579 0.797296 15.1894 2.39189C16.921 3.98645 17.7867 5.93492 17.7867 8.23728C17.7867 9.26149 17.5845 10.2704 17.1802 11.2642C16.7758 12.2579 16.2231 13.1069 15.5222 13.8113L25.7937 23.3585L25.1466 24ZM8.9306 15.6604C11.1705 15.6604 13.0582 14.9497 14.5938 13.5283C16.1293 12.1069 16.8971 10.3459 16.8971 8.24528C16.8971 6.14465 16.1293 4.38365 14.5938 2.96226C13.0582 1.54088 11.1705 0.830189 8.9306 0.830189C6.69074 0.830189 4.80302 1.54088 3.26744 2.96226C1.7319 4.38365 0.964121 6.14465 0.964121 8.24528C0.964121 10.3459 1.7319 12.1069 3.26744 13.5283C4.80302 14.9497 6.69074 15.6604 8.9306 15.6604Z"
            fill="#1C1C20"
          />
        </svg>
      </div>
      <input
        type="text"
        bind:value={searchTerm}
        placeholder="Search for Task"
      />
    </div>
  </div>
  <div class="bar_wrapper">
    {#if loading}
      {#if enableSorter}
        <div class="button-wrapper">
          <div class:sorter-button={true}>
            <ButtonNew
              setting
              text="Sort by name"
              icon="sort-arrow"
              iconRight
              secondary
              disabled
            ></ButtonNew>
          </div>
          <div class:sorter-button={true}>
            <ButtonNew
              setting
              text={`Sort by ${valueLabel}`}
              icon="sort-arrow"
              iconRight
              secondary
              disabled
            ></ButtonNew>
          </div>
        </div>
      {/if}
      {#each Array(5) as _, index}
        <div class="appinfo_wrapper disable">
          <SideKPI loading></SideKPI>
        </div>
      {/each}
    {:else}
      {#if enableSorter && displayedTaskGroups[0]?.subtasks[0]?.name}
        <div class="button-wrapper">
          <div
            class:sorter-button={true}
            class:sorter-desc={sorterByName === "desc"}
            class:sorter-asc={sorterByName === "asc"}
          >
            <ButtonNew
              setting
              text="Sort by name"
              icon={sorterByName ? "sorted-arrow2" : "sort-arrow"}
              iconRight
              secondary
              on:click={() => {
                nameSorter();
              }}
            ></ButtonNew>
          </div>
          <div
            class:sorter-button={true}
            class:sorter-desc={sorterByNumber === "desc"}
            class:sorter-asc={sorterByNumber === "asc"}
          >
            <ButtonNew
              setting
              text={`Sort by ${valueLabel}`}
              icon={sorterByNumber ? "sorted-arrow2" : "sort-arrow"}
              iconRight
              secondary
              on:click={() => {
                valueSorter();
              }}
            ></ButtonNew>
          </div>
        </div>
      {/if}
      <div class="task_topline">
        <span title={taskToplineText}>{taskToplineText}</span>
        <div class="task_topline_actions">
          {#if showFrequencyMapping}
            <button
              type="button"
              class="task_frequency_button"
              title="Task KPI Setup"
              aria-label="Task KPI Setup"
              on:click={() => dispatch("frequencyclick")}
            >
              <img src="/img/assistant/icons/event repeat.svg" alt="" />
            </button>
          {/if}
          <button
            type="button"
            class="task_settings_button"
            class:active={showUnavailableTasks}
            title={showUnavailableTasks
              ? "Hide unavailable tasks"
              : "Show unavailable tasks"}
            aria-label={showUnavailableTasks
              ? "Hide unavailable tasks"
              : "Show unavailable tasks"}
            on:click={() => (showUnavailableTasks = !showUnavailableTasks)}
          >
            <img src="/img/assistant/icons/filter_alt.svg" alt="" />
          </button>
          <button
            type="button"
            class="task_settings_button"
            title="Task grouping"
            aria-label="Task grouping"
            on:click={() => (taskSettingsOpen = !taskSettingsOpen)}
          >
            <img src="/img/assistant/icons/category.svg" alt="" />
          </button>
        </div>
      </div>
      {#if taskSettingsOpen}
        <div class="task_settings">
          <span>Group by</span>
          {#each ["L1", "L2", "L3"] as level}
            <button
              type="button"
              class:active={taskGroupingLevel === level}
              on:click={() => (taskGroupingLevel = level)}
            >
              {level}
            </button>
          {/each}
        </div>
      {/if}
      {#each displayedTaskGroups as group}
        <div class="task_group">
          <button
            type="button"
            class="task_group_row"
            on:click={() => toggleTaskGroup(group.id)}
          >
            <img
              class="task_group_icon"
              src="/img/assistant/icons/chevron_{group.open
                ? 'down'
                : 'right'}_black.svg"
              alt=""
            />
            <span title={group.name}>{group.name}</span>
            <div class="task_group_count">
              <Tooltip text={formatTaskGroupTooltip(group)} />
            </div>
          </button>
          {#if group.open}
            <div class="task_group_items">
              {#each group.subtasks as task}
                <!-- svelte-ignore missing-declaration -->
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                  class="kpi_wrapper appinfo_wrapper"
                  class:selected-task={task.id === currentSubtask}
                  class:disable={task.disable}
                  title={task.disable
                    ? `At least ${disableThreshold} registrations required`
                    : ""}
                  on:click={() => {
                    if (!task.disable) {
                      selectTask(task);
                    }
                  }}
                >
                  <SideKPI
                    name={task.name || task}
                    value={formatTaskValue(task.value)}
                    disable={task.disable}
                  >
                    <Tooltip
                      slot="value"
                      text={formatTaskValueTooltip(task.value)}
                    />
                  </SideKPI>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/each}
    {/if}
  </div>

  <AccordionPagination
    item="Apps"
    total={taskGroupCount}
    bind:currentpage={currentPage}
    itemsPerPage={displayCount}
    disablePrev={currentPage === 1}
    disableNext={currentPage >= Math.ceil(taskGroupCount / displayCount)}
  />
</div>

<style>
  .appinfo_wrapper {
    cursor: pointer;
    align-items: center;
    height: 2.125rem;
    border-radius: 0.5rem;
    border: 1px solid var(--pax-smoke);
  }

  :global(.appinfo_wrapper > .kpi_wrapper) {
    height: 100%;
    padding: 0 1rem;
  }

  .task_group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .task_topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    color: #5f6770;
    font-size: 0.75rem;
  }

  .task_topline > span {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .task_topline_actions {
    flex: 0 0 auto;
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .task_settings_button,
  .task_frequency_button {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.8rem;
    height: 1.8rem;
    border: 0;
    border-radius: 0.375rem;
    background: var(--pax-black);
    cursor: pointer;
  }

  .task_settings_button img,
  .task_frequency_button img {
    width: 0.9rem;
    height: 0.9rem;
  }

  .task_settings_button.active {
    background: #ff4f1f;
  }

  .task_settings {
    display: grid;
    grid-template-columns: auto repeat(3, 1fr);
    align-items: center;
    gap: 0.4rem;
    color: #5f6770;
    font-size: 0.75rem;
  }

  .task_settings button {
    height: 1.5rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.35rem;
    background: white;
    cursor: pointer;
    font-weight: 700;
  }

  .task_settings button.active {
    border-color: #ff4f1f;
    color: #ff4f1f;
  }

  .task_group_row {
    display: grid;
    grid-template-columns: 1rem minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.5rem;
    height: 2.125rem;
    border-radius: 0.5rem;
    border: none;
    background: var(--pax-smoke);
    padding: 0 1rem 0 0.65rem;
    text-align: left;
    cursor: pointer;
    font-size: inherit;
  }

  .task_group_row span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: inherit;
  }

  :global(.task_group_items .type) {
    font-size: inherit;
  }

  :global(.task_group_items .kpi_wrapper) {
    font-size: inherit;
  }

  .task_group_count {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    white-space: nowrap;
  }

  .task_group_icon {
    width: 0.8rem;
    height: 0.8rem;
    justify-self: center;
  }

  .task_group_items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-left: 1.35rem;
    padding-left: 0.8rem;
    border-left: 1px dotted #c7ccd2;
  }

  :global(.task_group_items .appinfo_wrapper > .kpi_wrapper .value) {
    font-weight: 400;
  }

  :global(.task_group_items .appinfo_wrapper.disable > .kpi_wrapper .type),
  :global(.task_group_items .appinfo_wrapper.disable > .kpi_wrapper .value) {
    font-weight: 400;
  }

  :global(.task_group_items .appinfo_wrapper > .kpi_wrapper .left_wrapper),
  :global(.task_group_items .appinfo_wrapper > .kpi_wrapper .type) {
    text-align: left;
  }

  .bar_wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #d9d9d9;
  }

  .app_selection_wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .search_wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .search {
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    flex-grow: 1;
    display: flex;
    align-items: center;
    height: 3rem;
    padding: 0 1rem;
  }

  .searchicon {
    height: 1.5rem;
  }

  input {
    border: none;
    padding-left: 1rem;
    width: 90%;
  }

  .disable {
    color: var(--pax-black-inactive);
    cursor: default;
  }

  input:focus,
  input:active {
    outline: none;
  }

  .button-wrapper {
    display: flex;
    justify-content: space-evenly;
  }

  :global(.sorter-desc img) {
    transform: rotate(180deg);
    transform-origin: center;
  }

  :global(.button-wrapper > .sorter-button > .mainbutton) {
    height: 1rem !important;
    width: 100%;
  }
  .sorter-button {
    width: 49%;
  }

  .selected-task {
    border: 1px solid #ff6a1a;
    border-radius: 0.5rem;
    background: #fffaf6;
  }

  .appinfo_wrapper.disable {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
